import { ChevronRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  VisibilityDiagnosticPanel,
  type VisibilityDiagnostic,
} from '@/components/member-directory/VisibilityDiagnosticPanel'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON } from '@/lib/api'
import type { Person } from '@/lib/contacts/types'

interface DiagnosticResponse {
  member_directory_diagnostic: VisibilityDiagnostic
}

export function VisibilityDiagnosticSection({ person }: { person: Person }) {
  const query = useQuery({
    queryKey: ['member-directory-diagnostic', person.id],
    queryFn: () => getJSON<DiagnosticResponse>(`/api/v1/member_directory_diagnostic/${person.id}`),
    enabled: Boolean(person.membership),
    retry: false,
  })

  if (!person.membership) {
    return (
      <DiagnosticContainer
        diagnostic={{ message: 'The Member Directory lists members only.', conditions: [] }}
      />
    )
  }
  if (query.isLoading) {
    return (
      <EmptyState
        title="Loading directory visibility"
        className="mt-6 max-w-2xl items-start px-0 text-left"
      />
    )
  }
  if (query.isError || !query.data) return null

  return <DiagnosticContainer diagnostic={query.data.member_directory_diagnostic} />
}

function DiagnosticContainer({ diagnostic }: { diagnostic: VisibilityDiagnostic }) {
  return (
    <Collapsible className="group/directory mt-6 max-w-2xl">
      <h2 className="text-body font-medium text-foreground">
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-data-[state=open]/directory:rounded-b-none group-data-[state=open]/directory:border-b-0">
          <ChevronRight
            className="size-4 shrink-0 transition-transform group-data-[state=open]/directory:rotate-90 motion-reduce:transition-none"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          Directory visibility
        </CollapsibleTrigger>
      </h2>
      <CollapsibleContent className="[&>div]:rounded-t-none [&>div]:border-t-0">
        <VisibilityDiagnosticPanel diagnostic={diagnostic} showTitle={false} />
      </CollapsibleContent>
    </Collapsible>
  )
}
